/**
 * Tokenized invitation client (DEV-1307 / onboarding Slice C).
 *
 * The NEW-user (external) invite path: POST /api/invitations creates a
 * tokenized invitation and returns a copyable accept link (§7.2 — generated
 * link, manual send). Distinct from the existing-user org invitation, which
 * rides the workflow membership-invitation flow (membershipAdmission.ts).
 * Option A (the invite box) picks between them client-side.
 */

import { apiFetch } from './client';

export interface CreateOrgInvitationRequest {
  inviteeEmail: string;
  inviteeName?: string;
  /** Org community (required for org invites; omit for an L4E invite). */
  communityGuid?: string;
  roleSlotGuid?: string;
  personalMessage?: string;
}

export interface InvitationLink {
  invitationGuid: string;
  inviteeEmail: string;
  inviteeName?: string | null;
  /** The copyable accept path, e.g. "/invite/{token}". Empty when skipped. */
  inviteLink: string;
  expiresAt: string;
  /** Set when the row was not created (e.g. already registered). */
  skipped?: string | null;
}

/** Create one tokenized invitation and get its copyable link. */
export async function createOrgInvitation(
  req: CreateOrgInvitationRequest,
): Promise<InvitationLink> {
  return apiFetch<InvitationLink>('/api/invitations', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** A tokenized invitation as shown in the Sent-invitations list. The raw
 *  token / link is NEVER returned here (only its hash is stored) — use
 *  reissue to get a fresh copyable link. */
export interface SentInvitation {
  invitationGuid: string;
  inviterDisplayName: string;
  inviteeEmail: string | null;
  inviteeName: string | null;
  communityName: string | null;
  status: string;
  expiresAt: string;
  createdAt: string;
}

/** All tokenized invitations to an org (coordinator-scoped). */
export async function fetchCommunityInvitations(
  communityGuid: string,
): Promise<SentInvitation[]> {
  return apiFetch<SentInvitation[]>(
    `/api/invitations/by-community/${encodeURIComponent(communityGuid)}`,
  );
}

/** Mint a fresh token on a pending invitation and get a new copyable link
 *  (the original link is unrecoverable by design). */
export async function reissueInvitation(
  invitationGuid: string,
): Promise<InvitationLink> {
  return apiFetch<InvitationLink>(
    `/api/invitations/${encodeURIComponent(invitationGuid)}/reissue`,
    { method: 'POST' },
  );
}

/** Revoke a pending invitation. Frees the email for a fresh invite (a
 *  revoked row no longer blocks the duplicate-pending check). */
export async function revokeInvitation(invitationGuid: string): Promise<void> {
  await apiFetch(
    `/api/invitations/${encodeURIComponent(invitationGuid)}/revoke`,
    { method: 'POST' },
  );
}
